import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TextB, TextItalic, Link as LinkP, Image as ImageIcon, ListBullets, ListNumbers, ArrowCounterClockwise, ArrowClockwise } from '@phosphor-icons/react';
import { useEffect, useCallback } from 'react';

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.15)',
  bgHover: 'rgba(247,246,242,0.06)',
};

interface TiptapEditorProps {
  content: any;
  onChange: (json: any) => void;
  onImageUpload?: () => void;
}

export function TiptapEditor({ content, onChange, onImageUpload }: TiptapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'tiptap-link' } }),
      Image.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({ placeholder: 'Comece a escrever...' }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
    editorProps: {
      attributes: {
        style: `font-family: ${monoFont}; font-size: 14px; font-weight: 300; color: ${C.fg}; min-height: 200px; outline: none; padding: 16px; line-height: 1.7;`,
      },
    },
  });

  // Sync external content changes
  useEffect(() => {
    if (editor && content && typeof content === 'object' && content.type === 'doc') {
      const currentJSON = JSON.stringify(editor.getJSON());
      const newJSON = JSON.stringify(content);
      if (currentJSON !== newJSON) {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL do link:', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    if (onImageUpload) {
      onImageUpload();
      return;
    }
    const url = window.prompt('URL da imagem:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor, onImageUpload]);

  if (!editor) return null;

  const ToolBtn = ({ onClick, active, children, title }: { onClick: () => void; active?: boolean; children: React.ReactNode; title: string }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        padding: '4px 6px',
        backgroundColor: active ? C.bgHover : 'transparent',
        border: `1px solid ${active ? C.border : 'transparent'}`,
        color: active ? C.fg : C.fgMuted,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </button>
  );

  const chain = () => editor.chain() as any;

  return (
    <div style={{ border: `1px solid ${C.border}` }}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-2" style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: 'rgba(247,246,242,0.02)' }}>
        <ToolBtn onClick={() => chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrito">
          <TextB size={14} weight="thin" />
        </ToolBtn>
        <ToolBtn onClick={() => chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Itálico">
          <TextItalic size={14} weight="thin" />
        </ToolBtn>
        <div style={{ width: 1, height: 16, backgroundColor: C.border, margin: '0 4px' }} />
        <ToolBtn onClick={addLink} active={editor.isActive('link')} title="Link">
          <LinkP size={14} weight="thin" />
        </ToolBtn>
        <ToolBtn onClick={addImage} title="Imagem">
          <ImageIcon size={14} weight="thin" />
        </ToolBtn>
        <div style={{ width: 1, height: 16, backgroundColor: C.border, margin: '0 4px' }} />
        <ToolBtn onClick={() => chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista">
          <ListBullets size={14} weight="thin" />
        </ToolBtn>
        <ToolBtn onClick={() => chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerada">
          <ListNumbers size={14} weight="thin" />
        </ToolBtn>
        <div style={{ width: 1, height: 16, backgroundColor: C.border, margin: '0 4px' }} />
        <ToolBtn onClick={() => chain().focus().undo().run()} title="Desfazer">
          <ArrowCounterClockwise size={14} weight="thin" />
        </ToolBtn>
        <ToolBtn onClick={() => chain().focus().redo().run()} title="Refazer">
          <ArrowClockwise size={14} weight="thin" />
        </ToolBtn>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />
    </div>
  );
}
